import { create } from 'zustand';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const useWithdrawalStore = create((set, get) => ({
  // State
  networks: {},
  isLoadingNetworks: false,
  withdrawalFee: null,
  coinLimits: null,
  isLoadingFees: false,
  isLoadingLimits: false,
  withdrawalResponse: null,
  isLoadingWithdrawal: false,
  error: null,

  // Actions
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  // Fetch networks from API
  fetchNetworks: async () => {
    try {
      set({ isLoadingNetworks: true, error: null });
      
      const response = await fetch(`${API_BASE_URL}/v1/coins/`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const dynamicNetworks = {};
      const blockchainDefinitions = data.commons?.blockchain_definitions || {};

      // Process each coin
      Object.entries(data.coins).forEach(([coinSymbol, coinData]) => {
        const coinNetworks = [];

        if (coinData.is_token) {
          // For tokens, use blockchain_list to generate networks
          if (coinData.blockchain_list && coinData.blockchain_list.length > 0) {
            coinData.blockchain_list.forEach(blockchain => {
              const networkName = blockchainDefinitions[blockchain] || blockchain;
              coinNetworks.push({
                name: `${networkName} (${blockchain})`,
                symbol: blockchain,
                fee: null,
                confirmations: null,
                minDeposit: null
              });
            });
          }
        } else {
          // For base coins, use the coin name as network
          coinNetworks.push({
            name: coinData.name,
            symbol: coinSymbol,
            fee: null,
            confirmations: null,
            minDeposit: null
          });
        }

        if (coinNetworks.length > 0) {
          dynamicNetworks[coinSymbol] = coinNetworks;
        }
      });

      set({ networks: dynamicNetworks, isLoadingNetworks: false });
      return dynamicNetworks;
    } catch (error) {
      console.error('Error fetching networks:', error);
      set({ 
        error: 'Failed to fetch networks', 
        networks: {}, 
        isLoadingNetworks: false 
      });
      return {};
    }
  },

  // Fetch withdrawal fees for a specific coin
  fetchWithdrawalFee: async (coinSymbol) => {
    try {
      set({ isLoadingFees: true, error: null });
      
      const response = await fetch(`${API_BASE_URL}/v1/withdrawalfees/${coinSymbol}/`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const fee = data[coinSymbol];
      
      set({ 
        withdrawalFee: fee, 
        isLoadingFees: false 
      });
      
      return fee;
    } catch (error) {
      console.error('Error fetching withdrawal fee:', error);
      set({ 
        error: 'Failed to fetch withdrawal fee', 
        withdrawalFee: null, 
        isLoadingFees: false 
      });
      return null;
    }
  },

  // Fetch coin limits and fees
  fetchCoinLimits: async (coinSymbol) => {
    try {
      set({ isLoadingLimits: true, error: null });
      
      const response = await fetch(`${API_BASE_URL}/v1/limits/`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const limits = data[coinSymbol];
      
      set({ 
        coinLimits: limits, 
        isLoadingLimits: false 
      });
      
      return limits;
    } catch (error) {
      console.error('Error fetching coin limits:', error);
      set({ 
        error: 'Failed to fetch coin limits', 
        coinLimits: null, 
        isLoadingLimits: false 
      });
      return null;
    }
  },

  // Submit withdrawal request
  submitWithdrawal: async (withdrawalData) => {
    try {
      set({ isLoadingWithdrawal: true, error: null });
      
      const response = await fetch(`${API_BASE_URL}/v1/withdrawal/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(withdrawalData)
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('Withdrawal error response:', responseData);
        
        // Handle validation errors with detailed messages
        let errorMessage = 'Withdrawal request failed';
        if (responseData.message && Array.isArray(responseData.message) && responseData.message.length > 0) {
          errorMessage = responseData.message[0].message || errorMessage;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        }
        
        throw new Error(errorMessage);
      }

      set({ 
        withdrawalResponse: responseData, 
        isLoadingWithdrawal: false 
      });
      
      return { success: true, data: responseData };
    } catch (error) {
      console.error('Error submitting withdrawal:', error);
      set({ 
        error: error.message || 'Failed to submit withdrawal request', 
        isLoadingWithdrawal: false 
      });
      return { success: false, error: error.message };
    }
  },

  // Get available networks for a specific coin (with fallback)
  getAvailableNetworks: (coinSymbol) => {
    const { networks } = get();
    const availableNetworks = networks[coinSymbol] || [];
    
    // If no networks available from API, use the coin itself as a network
    if (coinSymbol && availableNetworks.length === 0) {
      return [{
        name: coinSymbol,
        symbol: coinSymbol,
        fee: null,
        confirmations: null,
        minDeposit: null
      }];
    }
    
    return availableNetworks;
  },

  // Get network-specific withdrawal fee
  getNetworkWithdrawalFee: (coinSymbol, networkSymbol) => {
    const { coinLimits } = get();
    if (!coinLimits || !networkSymbol) return null;

    const withdrawalFees = coinLimits.fee?.withdrawal?.address;
    if (!withdrawalFees) return null;

    // For USDT, check if the network exists in withdrawal fees
    if (coinSymbol === 'USDT') {
      if (networkSymbol === 'ETH') return withdrawalFees.ETH;
      if (networkSymbol === 'BNB') return withdrawalFees.BNB;
      if (networkSymbol === 'TRX') return withdrawalFees.TRX;
    }
    
    // For other coins, return the fee for the coin itself
    return withdrawalFees[coinSymbol] || withdrawalFees[networkSymbol];
  },

  // Get max withdrawal amount
  getMaxWithdrawal: () => {
    const { coinLimits } = get();
    if (!coinLimits) return '0';
    return coinLimits.limits?.withdrawal?.max || '0';
  },

  // Get min withdrawal amount
  getMinWithdrawal: () => {
    const { coinLimits } = get();
    if (!coinLimits) return '0';
    return coinLimits.limits?.withdrawal?.min || '0';
  },

  // Reset withdrawal response
  resetWithdrawalResponse: () => set({ withdrawalResponse: null }),

  // Validate withdrawal address
  validateAddress: async (currency, address, network) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/withdrawal/validate-address/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ currency, address, network })
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Address validation error:', error);
      return { is_valid: false, message: 'Validation failed' };
    }
  },

  // Calculate withdrawal fee
  calculateWithdrawalFee: async (currency, network, amount) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/withdrawal/calculate-fee/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ currency, network, amount })
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fee calculation error:', error);
      return null;
    }
  },

  // Reset all state
  reset: () => set({
    networks: {},
    isLoadingNetworks: false,
    withdrawalFee: null,
    coinLimits: null,
    isLoadingFees: false,
    isLoadingLimits: false,
    withdrawalResponse: null,
    isLoadingWithdrawal: false,
    error: null
  })
}));

export default useWithdrawalStore;
